/* MigrationProgressBeacon — global floating progress nudge for new users.
 *
 * Shows a small chip in the bottom-right of every authenticated page
 * (stacked above the FloatingCFOBuck FAB) until the user reaches 100% on
 * their onboarding/migration checklist. Then shows ONE celebratory state
 * for a few seconds and disappears.
 *
 * Polls /api/migration/progress every 60s. Light-weight: pure DB counts,
 * no LLM. The endpoint itself fires the 100%-welcome email exactly once.
 *
 * UX rules:
 *   • Hidden on public/auth/welcome/terms/portal routes
 *   • Hidden when percent === 100 AND celebration shown for >5s
 *   • User-dismissible for 24h (localStorage)
 *   • Click-to-expand reveals all step states + deeplinks
 *   • Auto-hides during the trial-gate redirect chain so the gate banner
 *     doesn't fight with us for the same screen real estate
 */
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiFetch } from '../fetchInterceptor';
import { useAuth } from '../context/AuthContext';
import {
  Sparkles, Check, ChevronUp, ChevronDown, X, ArrowRight, PartyPopper,
} from 'lucide-react';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Routes where the beacon should be silent (login, public marketing,
// trial-gate retry, portal-only client experience).
const SKIP_PREFIXES = [
  '/login', '/welcome', '/auth-callback',
  '/terms', '/privacy', '/features', '/portal',
  '/sign/', '/owner-admin',
];

const DISMISS_KEY = 'migration_beacon_dismissed_until';
const POLL_MS = 60_000;

export default function MigrationProgressBeacon() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Fetch workspace_id ourselves (Floating beacons render outside
  // WorkspaceProvider so we can't use the context hook).
  const [workspaceId, setWorkspaceId] = useState(null);
  useEffect(() => {
    if (!user) return;
    let alive = true;
    (async () => {
      try {
        const r = await apiFetch(`${API_URL}/api/workspaces`);
        if (!r.ok) return;
        const ws = await r.json();
        // Pick the first owned workspace. Client-portal-only users
        // shouldn't see the beacon (they're not the ones migrating).
        const owned = (ws || []).filter((w) => w.role === 'owner' || w.role === 'admin');
        if (alive && owned.length > 0) setWorkspaceId(owned[0].workspace_id);
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [user]);

  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  // Lazy init from localStorage to avoid setState-in-effect lint warning.
  const [dismissed, setDismissed] = useState(() => {
    try {
      const until = parseInt(window.localStorage.getItem(DISMISS_KEY) || '0', 10);
      return Boolean(until && Date.now() < until);
    } catch { return false; }
  });
  // Tracks whether we've shown the 100% celebration card long enough to
  // auto-dismiss (5s after first hit).
  const [celebrationDoneAt, setCelebrationDoneAt] = useState(null);

  const skip = useMemo(() => {
    const p = location.pathname;
    if (p === '/') return true;
    return SKIP_PREFIXES.some((pre) => p.startsWith(pre));
  }, [location.pathname]);

  // Poll the progress endpoint.
  useEffect(() => {
    if (skip || !workspaceId) return;
    let alive = true;
    const tick = async () => {
      try {
        const r = await apiFetch(`${API_URL}/api/migration/progress?workspace_id=${workspaceId}`);
        if (!r.ok) return;
        const d = await r.json();
        if (alive) setData(d);
      } catch { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, [skip, workspaceId]);

  // Trigger the 5-second auto-dismiss of the celebration card.
  useEffect(() => {
    if (data?.percent === 100 && celebrationDoneAt === null) {
      const t = setTimeout(() => setCelebrationDoneAt(Date.now()), 5000);
      return () => clearTimeout(t);
    }
  }, [data?.percent, celebrationDoneAt]);

  const dismiss = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now() + 24 * 60 * 60 * 1000));
    } catch { /* ignore */ }
    setDismissed(true);
  };

  // ── Gate the render ─────────────────────────────────────────────
  if (skip) return null;
  if (!workspaceId) return null;
  if (!data) return null;
  if (dismissed) return null;
  // 100% with celebration shown long enough → hide permanently for this session.
  if (data.percent === 100 && celebrationDoneAt) return null;

  // ── Celebratory 100% card ───────────────────────────────────────
  if (data.percent === 100) {
    return (
      <div
        data-testid="migration-beacon-done"
        className="fixed top-20 right-6 z-50 w-[320px] rounded-xl border border-emerald-500/30 bg-emerald-500/10 backdrop-blur-md shadow-2xl shadow-emerald-500/10 p-4 animate-in slide-in-from-top-3"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-500/20 text-emerald-300 flex items-center justify-center flex-shrink-0">
            <PartyPopper className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-emerald-100">You&apos;re 100% migrated.</p>
            <p className="text-xs text-emerald-200/80 mt-0.5 leading-relaxed">
              Welcome email sent. Connect anything else from Settings whenever you&apos;re ready.
            </p>
          </div>
          <button
            onClick={dismiss}
            data-testid="migration-beacon-done-close"
            className="text-emerald-200/60 hover:text-white"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // ── In-progress beacon ──────────────────────────────────────────
  // Positioned top-RIGHT just under the header so it never covers the
  // bottom-right content, Quick Actions, or the chat bubble. z-50 matches
  // the rest of the floating UI; we don't go higher to avoid covering
  // modals / sheets.
  const next = data.next_step;
  return (
    <div
      data-testid="migration-beacon"
      className={cn(
        'fixed top-20 right-6 z-50 rounded-xl border border-white/10 bg-zinc-950/85 backdrop-blur-xl shadow-2xl shadow-black/40 transition-all',
        expanded ? 'w-[360px]' : 'w-[300px]',
      )}
    >
      {/* Collapsed header — always visible */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full p-3 flex items-center gap-3 text-left"
        data-testid="migration-beacon-toggle"
      >
        <div className="w-9 h-9 rounded-lg bg-brand-500/20 text-brand-400 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[11px] font-mono uppercase tracking-wider text-zinc-500">
              Migration progress
            </p>
            <p className="text-xs font-mono font-bold text-brand-400">
              {data.percent}%
            </p>
          </div>
          {/* Progress bar */}
          <div className="mt-1.5 h-1.5 rounded-full bg-white/5 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-brand-500 to-emerald-400 transition-all duration-500"
              style={{ width: `${data.percent}%` }}
              data-testid="migration-beacon-bar"
            />
          </div>
          {next && !expanded && (
            <p className="text-xs text-zinc-300 mt-1.5 truncate">
              Next: <span className="text-white font-medium">{next.label}</span>
            </p>
          )}
        </div>
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-zinc-500" />
        ) : (
          <ChevronUp className="w-4 h-4 text-zinc-500" />
        )}
      </button>

      {/* Expanded step list */}
      {expanded && (
        <div className="px-3 pb-3 space-y-1.5" data-testid="migration-beacon-steps">
          {data.steps.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => s.deeplink && navigate(s.deeplink)}
              disabled={!s.deeplink || s.done}
              data-testid={`migration-beacon-step-${s.id}`}
              className={cn(
                'w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md text-xs text-left transition-colors',
                s.done
                  ? 'text-zinc-500'
                  : 'text-zinc-200 hover:bg-white/5 cursor-pointer',
              )}
            >
              <div className={cn(
                'w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0',
                s.done
                  ? 'bg-emerald-500/30 text-emerald-300'
                  : 'border border-white/15',
              )}>
                {s.done && <Check className="w-2.5 h-2.5" />}
              </div>
              <span className={cn('flex-1 truncate', s.done && 'line-through')}>
                {s.label}
              </span>
              {!s.done && s.deeplink && (
                <ArrowRight className="w-3 h-3 text-zinc-500" />
              )}
            </button>
          ))}
          <div className="pt-2 mt-2 border-t border-white/5 flex items-center justify-between">
            <button
              type="button"
              onClick={() => navigate('/switch-from-quickbooks')}
              data-testid="migration-beacon-open-hub"
              className="text-[11px] text-brand-400 hover:text-brand-300 font-medium"
            >
              Open migration hub →
            </button>
            <button
              type="button"
              onClick={dismiss}
              data-testid="migration-beacon-snooze"
              className="text-[11px] text-zinc-500 hover:text-zinc-300"
            >
              Hide for 24h
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
